import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Facebook, MessageCircle, Loader2, Copy, Plug, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

const PROVIDERS = {
  meta: {
    label: "Meta Lead Ads",
    icon: Facebook,
    blurb: "Capture leads instantly from Facebook & Instagram lead forms. Leads auto-route into your pipeline.",
    fields: [
      { key: "page_id", label: "Page ID", secret: false },
      { key: "page_name", label: "Page Name", secret: false },
      { key: "verify_token", label: "Webhook Verify Token", secret: false },
      { key: "page_access_token", label: "Page Access Token", secret: true },
    ],
  },
  whatsapp: {
    label: "WhatsApp Business",
    icon: MessageCircle,
    blurb: "Send order updates, invoices and reminders to customers on WhatsApp via the Cloud API.",
    fields: [
      { key: "phone_number_id", label: "Phone Number ID", secret: false },
      { key: "business_id", label: "Business Account ID", secret: false },
      { key: "access_token", label: "Access Token", secret: true },
    ],
  },
};

function StatusPill({ status, active }) {
  const map = {
    active: { c: "bg-success/10 text-success border-success/20", t: "Active" },
    configured: { c: "bg-info/10 text-info border-info/20", t: "Configured" },
    not_configured: { c: "bg-muted text-muted-foreground border-border", t: "Not configured" },
  };
  const s = active ? map.active : map[status] || map.not_configured;
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${s.c}`}>{s.t}</span>;
}

function IntegrationCard({ data, canEdit }) {
  const qc = useQueryClient();
  const meta = PROVIDERS[data.provider];
  const Icon = meta.icon;
  const [form, setForm] = useState({});

  useEffect(() => {
    const init = {};
    meta.fields.forEach((f) => { init[f.key] = f.secret ? "" : (data.config?.[f.key] || ""); });
    setForm(init);
  }, [data.provider]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveMut = useMutation({
    mutationFn: () => {
      const config = {};
      meta.fields.forEach((f) => { if (form[f.key]) config[f.key] = form[f.key]; });
      return api.put(`/integrations/${data.provider}`, { config });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["integrations"] }); qc.invalidateQueries({ queryKey: ["integration-logs"] }); toast.success(`${meta.label} saved`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Save failed"),
  });

  const activateMut = useMutation({
    mutationFn: (active) => api.post(`/integrations/${data.provider}/activate`, { active }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["integrations"] }); qc.invalidateQueries({ queryKey: ["integration-logs"] }); toast.success(res.data.active ? "Activated" : "Deactivated"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not change status"),
  });

  const testMut = useMutation({
    mutationFn: () => api.post(`/integrations/${data.provider}/test`),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: ["integration-logs"] }); res.data.ok ? toast.success(res.data.message) : toast.error(res.data.message); },
    onError: (e) => toast.error(e.response?.data?.detail || "Test failed"),
  });

  const copyWebhook = () => { navigator.clipboard.writeText(data.webhook_url); toast.success("Webhook URL copied"); };

  return (
    <Card className="p-6" data-testid={`integration-card-${data.provider}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-heading text-base font-semibold">{meta.label}</p>
              <StatusPill status={data.status} active={data.active} />
            </div>
            <p className="mt-1 max-w-md text-sm text-muted-foreground">{meta.blurb}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">{data.active ? "On" : "Off"}</Label>
          <Switch
            checked={data.active}
            disabled={!canEdit || activateMut.isPending}
            onCheckedChange={(v) => activateMut.mutate(v)}
            data-testid={`integration-toggle-${data.provider}`}
          />
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {meta.fields.map((f) => (
          <div key={f.key} className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs">
              {f.label}{f.secret && <ShieldCheck className="h-3 w-3 text-muted-foreground" />}
            </Label>
            <Input
              type={f.secret ? "password" : "text"}
              value={form[f.key] || ""}
              disabled={!canEdit}
              placeholder={f.secret && data.config?.[f.key] ? `Saved · ${data.config[f.key]}` : ""}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              data-testid={`integration-${data.provider}-${f.key}-input`}
            />
          </div>
        ))}
      </div>

      {data.webhook_url && (
        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">Webhook Callback URL (add this in Meta App → Webhooks)</p>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1.5 text-xs" data-testid={`integration-webhook-${data.provider}`}>{data.webhook_url}</code>
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={copyWebhook} data-testid={`copy-webhook-${data.provider}`}><Copy className="h-4 w-4" /></Button>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid={`integration-save-${data.provider}`}>
            {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save credentials"}
          </Button>
          <Button variant="outline" onClick={() => testMut.mutate()} disabled={testMut.isPending} data-testid={`integration-test-${data.provider}`}>
            {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Test connection"}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function Integrations() {
  const { user } = useAuth();
  const canEdit = user?.role !== "customer";
  const { data: integrations = [], isLoading } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/integrations")).data });
  const { data: logs = [] } = useQuery({ queryKey: ["integration-logs"], queryFn: async () => (await api.get("/integrations/logs")).data, refetchInterval: 20000 });

  return (
    <div>
      <PageHeader title="Integrations" subtitle="Connect lead sources and messaging channels" testid="integrations-header" />
      {isLoading ? <Loading /> : (
        <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
          <div className="space-y-5">
            {integrations.map((it) => <IntegrationCard key={it.provider} data={it} canEdit={canEdit} />)}
          </div>

          <Card className="h-fit p-5" data-testid="integration-activity">
            <div className="mb-3 flex items-center gap-2">
              <Plug className="h-4 w-4 text-primary" />
              <p className="font-heading text-sm font-semibold">Activity Log</p>
            </div>
            {logs.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No activity yet</p>
            ) : (
              <div className="max-h-[60vh] space-y-3 overflow-y-auto">
                {logs.map((l) => (
                  <div key={l.id} className="flex items-start gap-2 border-b border-border pb-3 last:border-0" data-testid={`integration-log-${l.id}`}>
                    {l.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-critical" />}
                    <div className="min-w-0">
                      <p className="text-sm"><span className="font-medium capitalize">{l.provider}</span> · {l.event}</p>
                      <p className="text-xs text-muted-foreground">{l.detail}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
